// src/utils/storyEnginePrompts.js

import {
  WRITING_MODE,
  STORY_SECTIONS,
  FIELD_STATUS,
  REVIEW_REASON,
  CHAPTER_KEYS,
} from './storyEngineSchema';

/* =========================================================
   GLOBAL WRITING RULES
   ========================================================= */

export const STORY_ENGINE_PROMPT_VERSION = 'v1';

export const GLOBAL_WRITING_GUARDRAILS = {
  brandIntent: [
    'Every drum should feel authored, intentional, and deeply personal.',
    'The writing should reflect a real build journey, not a generic product description.',
    'The language should feel human, grounded, elegant, and specific.',
    'The writing should never call attention to an engine, AI, automation, or generation process.',
    'The writing should support both the craftsman and the artist by translating intention into a coherent build story.',
  ],

  truthRules: [
    'Never present unsupported speculation as confirmed fact.',
    'If a detail is observed directly from consultation, questionnaire, or admin notes, it may be stated confidently.',
    'If a detail is inferred from multiple aligned signals, it may be written as a grounded interpretation rather than as a hard fact.',
    'If a detail is only a recommendation, it must sound like a thoughtful build direction, not a claim about the artist that may be untrue.',
    'When confidence is low, preserve honesty and avoid invented specifics.',
  ],

  toneRules: [
    'Write in a refined, warm, artisan voice.',
    'Avoid corporate language, marketing fluff, and overly dramatic phrasing.',
    'Avoid sounding robotic, clinical, or mechanically descriptive.',
    'Keep sentences clear, natural, and meaningful.',
    'Sound original and sincere, not like a template.',
  ],

  styleRules: [
    'Use concise paragraph structure.',
    'Keep each section thorough but tight.',
    'Favor specificity over adjective stacking.',
    'Use emotionally intelligent language without becoming cheesy.',
    'The result should feel premium, handcrafted, and believable.',
  ],

  forbiddenPhrases: [
    'AI-generated',
    'generated by AI',
    'our engine determined',
    'the algorithm suggests',
    'machine-generated',
    'based on machine analysis',
    'state-of-the-art AI',
    'customer persona',
    'ideal user',
    'leveraging data inputs',
    'best-in-class solution',
    'synergy',
    'seamless experience',
    'cutting-edge',
    'unlock potential',
  ],
};

export const WRITING_MODE_RULES = {
  [WRITING_MODE.TRUTH_ONLY]: {
    label: 'truth_only',
    instruction:
      'Use only observed or directly supported information. Do not add interpretation beyond what is clearly grounded in the source material.',
  },
  [WRITING_MODE.TRUTH_PLUS_INFERENCE]: {
    label: 'truth_plus_inference',
    instruction:
      'Use observed information first, then carefully bridge missing context with restrained, logical interpretation that clearly fits the rest of the build story.',
  },
  [WRITING_MODE.TRUTH_PLUS_RECOMMENDATION]: {
    label: 'truth_plus_recommendation',
    instruction:
      'Use observed information where available, then frame remaining gaps as thoughtful build direction or tonal intention rather than as claims about the artist.',
  },
  [WRITING_MODE.HOLD_FOR_REVIEW]: {
    label: 'hold_for_review',
    instruction:
      'Do not overstate certainty. If drafted at all, keep the language minimal, factual, and clearly dependent on later review.',
  },
};

/* =========================================================
   LENGTH TARGETS
   ========================================================= */

export const STORY_SECTION_LENGTHS = {
  [STORY_SECTIONS.CHAPTER_OVERVIEW]: {
    minWords: 55,
    idealWords: 85,
    maxWords: 120,
    preferredParagraphs: 1,
  },
  [STORY_SECTIONS.BUILD_NOTES_STORY]: {
    minWords: 70,
    idealWords: 105,
    maxWords: 140,
    preferredParagraphs: 1,
  },
};

export const UNIQUE_BUILD_TRAITS_LENGTH = {
  minItems: 2,
  idealItems: 3,
  maxItems: 4,
  maxWordsPerItem: 22,
};

/* =========================================================
   SECTION DEFINITIONS
   ========================================================= */

export const STORY_SECTION_DEFINITIONS = {
  [STORY_SECTIONS.CHAPTER_OVERVIEW]: {
    sectionLabel: 'Chapter Overview',
    purpose:
      'Summarize what this chapter means in the context of this specific artist, this specific drum, and this stage of the build.',
    shouldContain: [
      'What matters most in this chapter for this build',
      'Why this moment matters to the overall voice or identity of the drum',
      'A grounded sense of direction that feels personalized',
    ],
    shouldAvoid: [
      'Long technical lists',
      'Overly poetic abstraction with no connection to the actual build',
      'Repeating the same facts already obvious elsewhere',
    ],
  },

  [STORY_SECTIONS.BUILD_NOTES_STORY]: {
    sectionLabel: 'Build Notes Story',
    purpose:
      'Translate build reasoning into a short, honest, human story that helps explain the craft decisions and the unique identity taking shape in this chapter.',
    shouldContain: [
      'Specific build logic or material logic tied to the artist’s needs',
      'The unique identity of this drum in practical or emotional terms',
      'A sense of intentionality the craftsman can build from',
    ],
    shouldAvoid: [
      'Invented personal biography',
      'Claims the artist never made',
      'Generic drum language that could apply to anyone',
    ],
  },
};

/* =========================================================
   CHAPTER-SPECIFIC INTENT
   ========================================================= */

export const CHAPTER_PROMPT_PROFILES = {
  [CHAPTER_KEYS.DISCOVERY_DESIGN]: {
    chapterLabel: 'Discovery & Design',
    overviewFocus: [
      'the artist’s central need',
      'the emotional or sonic direction of the build',
      'the early identity of the drum',
    ],
    buildNotesFocus: [
      'what the artist is really asking for beneath surface preferences',
      'how the build should answer their playing context',
      'which early priorities must stay intact across the rest of the process',
    ],
  },

  [CHAPTER_KEYS.COMMITMENT_PORTAL]: {
    chapterLabel: 'Commitment Portal',
    overviewFocus: [
      'the moment the build direction becomes intentional',
      'what is being protected or committed to',
      'the alignment between artist vision and craftsman responsibility',
    ],
    buildNotesFocus: [
      'what decisions are now stable enough to guide the build',
      'which promises the build now needs to keep',
      'what should not drift as the drum develops',
    ],
  },

  [CHAPTER_KEYS.WOOD_VISION_LOCK_IN]: {
    chapterLabel: 'Wood Vision Lock-In',
    overviewFocus: [
      'material direction',
      'tonal identity through wood choice',
      'how look, feel, and sound begin to align materially',
    ],
    buildNotesFocus: [
      'why these wood choices make sense for the requested voice',
      'what the material pairing should contribute to attack, body, feel, or response',
      'how aesthetic and sonic goals reinforce one another',
    ],
  },

  [CHAPTER_KEYS.RAW_SHELL_CREATION]: {
    chapterLabel: 'Raw Shell Creation',
    overviewFocus: [
      'the shell as the foundation of the instrument’s voice',
      'how structure begins shaping tone and feel',
      'the importance of early physical integrity',
    ],
    buildNotesFocus: [
      'construction logic',
      'how shell form supports the target response',
      'what the craftsman should preserve as the shell takes shape',
    ],
  },

  [CHAPTER_KEYS.SHELL_TRUEING_TORCH_TUNE]: {
    chapterLabel: 'Shell Trueing, Torch & Tune',
    overviewFocus: [
      'precision, resonance, and shell readiness',
      'the move from raw structure toward controlled musical response',
      'how refinement affects the final voice',
    ],
    buildNotesFocus: [
      'what is being refined and why',
      'what tonal behavior must be encouraged or controlled',
      'where precision matters most for this artist’s target feel',
    ],
  },

  [CHAPTER_KEYS.EXTERIOR_ART_FINISH]: {
    chapterLabel: 'Exterior Art & Finish',
    overviewFocus: [
      'the visual identity of the drum',
      'how finish direction supports the story and personality of the build',
      'the relationship between appearance and character',
    ],
    buildNotesFocus: [
      'why this finish direction belongs to this drum',
      'how the visual treatment should feel in the artist’s hands or eyes',
      'what must stay tasteful, original, and aligned with the overall vision',
    ],
  },

  [CHAPTER_KEYS.EDGES_SNARE_BEDS]: {
    chapterLabel: 'Edges & Snare Beds',
    overviewFocus: [
      'the point where response becomes more exact',
      'the mechanical details that shape sensitivity and articulation',
      'how the instrument begins speaking more clearly',
    ],
    buildNotesFocus: [
      'why edge and snare bed decisions matter for the requested articulation',
      'what kind of feel and response is being targeted',
      'how these details serve both subtlety and authority',
    ],
  },

  [CHAPTER_KEYS.HARDWARE_ASSEMBLY]: {
    chapterLabel: 'Hardware Assembly',
    overviewFocus: [
      'the balance of reliability, feel, and visual completion',
      'how hardware choices support the drum’s final identity',
      'the moment function and form fully meet',
    ],
    buildNotesFocus: [
      'why the chosen hardware direction fits the build',
      'how assembly supports tuning stability, feel, and presentation',
      'what should feel cohesive and intentional in the finished instrument',
    ],
  },

  [CHAPTER_KEYS.LEGACY_TUNING_MEDIA]: {
    chapterLabel: 'Legacy, Tuning & Media',
    overviewFocus: [
      'the drum’s playable voice becoming fully apparent',
      'the artist-facing reveal of the instrument’s identity',
      'how the final sound is documented and presented',
    ],
    buildNotesFocus: [
      'how the tuning direction should present the drum honestly',
      'what aspects of the voice deserve emphasis',
      'how this chapter preserves the drum’s character for the artist and for its story',
    ],
  },

  [CHAPTER_KEYS.FINAL_QA_PACKAGING_DELIVERY]: {
    chapterLabel: 'Final QA, Packaging & Delivery',
    overviewFocus: [
      'completion, readiness, and trust',
      'the final responsibility of the build before handoff',
      'how the instrument leaves the shop with integrity',
    ],
    buildNotesFocus: [
      'what final checks matter most for this build',
      'what must be preserved in transit and presentation',
      'how the finished drum should arrive feeling complete, intentional, and worthy of its story',
    ],
  },
};

/* =========================================================
   STATUS LANGUAGE RULES
   ========================================================= */

export const STATUS_LANGUAGE_RULES = {
  [FIELD_STATUS.OBSERVED]: {
    allowedLanguage:
      'May be stated directly and confidently as part of the build story.',
  },
  [FIELD_STATUS.DERIVED]: {
    allowedLanguage:
      'May be presented as a grounded interpretation if it clearly fits the rest of the known data.',
  },
  [FIELD_STATUS.RECOMMENDED]: {
    allowedLanguage:
      'Should be framed as a build direction, tonal aim, or recommended path rather than a biographical truth.',
  },
  unknown: {
    allowedLanguage:
      'Should not be stated as fact. Either omit it or acknowledge it indirectly through broader intent.',
  },
};

/* =========================================================
   REVIEW / RISK LANGUAGE
   ========================================================= */

export const REVIEW_REASON_LANGUAGE = {
  [REVIEW_REASON.NOT_ENOUGH_DATA]:
    'Not enough grounded data exists to support a confident personalized statement.',
  [REVIEW_REASON.CONFLICTING_INPUT]:
    'Inputs appear to point in different directions and should be reconciled before strong wording is used.',
  [REVIEW_REASON.HIGH_VARIANCE_FIELD]:
    'This field varies too widely between players or contexts to safely infer without review.',
  [REVIEW_REASON.STORY_RISK]:
    'There is risk of writing something that feels untrue or overly assumptive to the artist.',
  [REVIEW_REASON.BUILD_RISK]:
    'There is risk of recommending a build choice that should be confirmed by the craftsman.',
  [REVIEW_REASON.RESEARCH_NEEDED]:
    'More external or domain-specific research may be needed before a strong recommendation is made.',
};

/* =========================================================
   GAP-FILLING RULES
   ========================================================= */

export const MISSING_DATA_WRITING_RULES = {
  corePrinciple:
    'When data is missing, fill the gap with the most logical and restrained interpretation supported by the strongest available signals, while protecting truth and believability.',

  allowedGapFillMoves: [
    'Bridge from playing context to tonal need',
    'Bridge from style/genre to response priorities',
    'Bridge from known size to likely feel or role',
    'Bridge from aesthetic direction to finish restraint or boldness',
    'Bridge from pain points to practical build priorities',
  ],

  disallowedGapFillMoves: [
    'Invent personal history',
    'Invent emotional statements the artist never made',
    'Invent very specific gear references without support',
    'Invent exact performance situations without support',
    'Write fake certainty into unclear material choices',
  ],

  preferredLanguagePatterns: [
    'This chapter centers on...',
    'What matters here is...',
    'The build is beginning to favor...',
    'At this stage, the goal is to preserve...',
    'The direction here is less about excess and more about...',
    'Everything in this step points toward...',
    'The intention is to keep...',
    'This choice supports a drum that feels...',
  ],

  recommendationLanguagePatterns: [
    'The build benefits from...',
    'The strongest direction here is...',
    'A natural fit for this build is...',
    'This points toward a shell that should...',
    'The most sensible material direction is...',
    'A tighter approach here helps preserve...',
  ],
};

/* =========================================================
   UNIQUE BUILD TRAITS RULES
   ========================================================= */

export const UNIQUE_BUILD_TRAITS_RULES = {
  purpose:
    'Generate a short set of bullet points describing what makes this specific build unique in practical, believable terms.',
  rules: [
    'Each bullet should describe a meaningful build distinction.',
    'Each bullet should be specific to this artist/build, not generic to handmade drums.',
    'Each bullet should connect a decision to a tonal, tactile, visual, or musical outcome.',
    'Each bullet should be concise and readable at a glance.',
    'Avoid repeating the same point in different words.',
  ],
  preferredCategories: [
    'shell voice',
    'response profile',
    'sensitivity and articulation',
    'projection and body',
    'finish identity',
    'hardware character',
    'tuning range behavior',
    'genre or use-case alignment',
  ],
  badExamples: [
    'Handcrafted with care',
    'Built to be unique',
    'Made for the artist',
    'Premium materials throughout',
  ],
};

/* =========================================================
   DRAFT PROMPT BUILDERS
   ========================================================= */

export function getGlobalPromptBlock() {
  return {
    version: STORY_ENGINE_PROMPT_VERSION,
    guardrails: GLOBAL_WRITING_GUARDRAILS,
    writingModes: WRITING_MODE_RULES,
    sectionDefinitions: STORY_SECTION_DEFINITIONS,
    missingDataRules: MISSING_DATA_WRITING_RULES,
    uniqueBuildTraitsRules: UNIQUE_BUILD_TRAITS_RULES,
  };
}

export function getSectionLength(sectionKey) {
  return STORY_SECTION_LENGTHS[sectionKey] || STORY_SECTION_LENGTHS.chapterOverview;
}

export function getChapterPromptProfile(chapterKey) {
  return (
    CHAPTER_PROMPT_PROFILES[chapterKey] || {
      chapterLabel: chapterKey,
      overviewFocus: [],
      buildNotesFocus: [],
    }
  );
}

export function getWritingModeInstruction(writingMode) {
  return (
    WRITING_MODE_RULES[writingMode]?.instruction ||
    WRITING_MODE_RULES[WRITING_MODE.HOLD_FOR_REVIEW].instruction
  );
}

export function getFieldStatusInstruction(status) {
  return (
    STATUS_LANGUAGE_RULES[status]?.allowedLanguage ||
    STATUS_LANGUAGE_RULES.unknown.allowedLanguage
  );
}

export function buildStorySectionPrompt({
  chapterKey,
  sectionKey,
  writingMode,
  resolvedFacts = [],
  reviewFacts = [],
  confidence = 0,
  lengthTarget = null,
}) {
  const chapterProfile = getChapterPromptProfile(chapterKey);
  const sectionDefinition = STORY_SECTION_DEFINITIONS[sectionKey];
  const sectionLength = lengthTarget || getSectionLength(sectionKey);

  return {
    chapterKey,
    chapterLabel: chapterProfile.chapterLabel,
    sectionKey,
    sectionLabel: sectionDefinition?.sectionLabel || sectionKey,
    confidence,
    writingMode,
    writingInstruction: getWritingModeInstruction(writingMode),
    sectionPurpose: sectionDefinition?.purpose || '',
    sectionShouldContain: sectionDefinition?.shouldContain || [],
    sectionShouldAvoid: sectionDefinition?.shouldAvoid || [],
    chapterFocus:
      sectionKey === STORY_SECTIONS.CHAPTER_OVERVIEW
        ? chapterProfile.overviewFocus
        : chapterProfile.buildNotesFocus,
    resolvedFacts: normalizeFactsForPrompt(resolvedFacts),
    reviewFacts: normalizeFactsForPrompt(reviewFacts),
    lengthTarget: sectionLength,
    globalGuardrails: getGlobalPromptBlock(),
  };
}

export function buildUniqueBuildTraitsPrompt({
  chapterKey,
  resolvedFacts = [],
  recommendations = [],
}) {
  const chapterProfile = getChapterPromptProfile(chapterKey);

  return {
    chapterKey,
    chapterLabel: chapterProfile.chapterLabel,
    purpose: UNIQUE_BUILD_TRAITS_RULES.purpose,
    rules: UNIQUE_BUILD_TRAITS_RULES.rules,
    preferredCategories: UNIQUE_BUILD_TRAITS_RULES.preferredCategories,
    length: UNIQUE_BUILD_TRAITS_LENGTH,
    resolvedFacts: normalizeFactsForPrompt(resolvedFacts),
    recommendations: recommendations || [],
  };
}

export function buildAdminReviewPrompt({
  chapterKey,
  sectionKey,
  reviewFacts = [],
}) {
  return {
    chapterKey,
    sectionKey,
    chapterLabel: getChapterPromptProfile(chapterKey).chapterLabel,
    sectionLabel:
      STORY_SECTION_DEFINITIONS[sectionKey]?.sectionLabel || sectionKey,
    reviewItems: normalizeFactsForPrompt(reviewFacts).map((fact) => ({
      fieldPath: fact.fieldPath,
      value: fact.value,
      status: fact.status,
      confidence: fact.confidence,
      reviewReasons: fact.reviewReasons,
      reviewReasonDescriptions: (fact.reviewReasons || []).map(
        (reason) => REVIEW_REASON_LANGUAGE[reason] || reason
      ),
    })),
  };
}

/* =========================================================
   RENDERABLE PROMPT TEXT HELPERS
   ========================================================= */

export function renderStoryPromptText(prompt) {
  const lines = [];

  lines.push(`Chapter: ${prompt.chapterLabel}`);
  lines.push(`Section: ${prompt.sectionLabel}`);
  lines.push(`Writing mode: ${prompt.writingMode}`);
  lines.push(`Confidence: ${formatConfidence(prompt.confidence)}`);
  lines.push('');
  lines.push(`Purpose: ${prompt.sectionPurpose}`);
  lines.push('');
  lines.push(`Instruction: ${prompt.writingInstruction}`);
  lines.push('');

  if (prompt.chapterFocus?.length) {
    lines.push('Focus this section on:');
    prompt.chapterFocus.forEach((item) => lines.push(`- ${item}`));
    lines.push('');
  }

  if (prompt.sectionShouldContain?.length) {
    lines.push('It should contain:');
    prompt.sectionShouldContain.forEach((item) => lines.push(`- ${item}`));
    lines.push('');
  }

  if (prompt.sectionShouldAvoid?.length) {
    lines.push('Avoid:');
    prompt.sectionShouldAvoid.forEach((item) => lines.push(`- ${item}`));
    lines.push('');
  }

  lines.push(
    `Length target: ${prompt.lengthTarget.minWords}-${prompt.lengthTarget.maxWords} words (ideal ${prompt.lengthTarget.idealWords})`
  );
  lines.push('');

  if (prompt.resolvedFacts?.length) {
    lines.push('Grounded facts available:');
    prompt.resolvedFacts.forEach((fact) => {
      lines.push(
        `- ${fact.fieldPath}: ${stringifyPromptValue(fact.value)} | ${fact.status} | confidence ${formatConfidence(fact.confidence)}`
      );
    });
    lines.push('');
  }

  if (prompt.reviewFacts?.length) {
    lines.push('Facts requiring restraint or review:');
    prompt.reviewFacts.forEach((fact) => {
      lines.push(
        `- ${fact.fieldPath}: ${stringifyPromptValue(fact.value)} | ${fact.status} | reasons: ${(fact.reviewReasons || []).join(', ')}`
      );
    });
    lines.push('');
  }

  lines.push('Global rules:');
  GLOBAL_WRITING_GUARDRAILS.truthRules.forEach((rule) => lines.push(`- ${rule}`));
  lines.push('');
  lines.push('Forbidden phrases:');
  GLOBAL_WRITING_GUARDRAILS.forbiddenPhrases.forEach((phrase) =>
    lines.push(`- ${phrase}`)
  );

  return lines.join('\n');
}

export function renderUniqueBuildTraitsPromptText(prompt) {
  const lines = [];

  lines.push(`Chapter: ${prompt.chapterLabel}`);
  lines.push('Task: Write concise unique build trait bullets.');
  lines.push('');
  lines.push(`Target bullet count: ${prompt.length.minItems}-${prompt.length.maxItems} (ideal ${prompt.length.idealItems})`);
  lines.push(`Max words per bullet: ${prompt.length.maxWordsPerItem}`);
  lines.push('');
  lines.push('Rules:');
  prompt.rules.forEach((rule) => lines.push(`- ${rule}`));
  lines.push('');

  if (prompt.resolvedFacts?.length) {
    lines.push('Use these grounded facts:');
    prompt.resolvedFacts.forEach((fact) => {
      lines.push(
        `- ${fact.fieldPath}: ${stringifyPromptValue(fact.value)} | ${fact.status}`
      );
    });
    lines.push('');
  }

  if (prompt.recommendations?.length) {
    lines.push('Optional recommended directions:');
    prompt.recommendations.forEach((item) => {
      lines.push(`- ${stringifyPromptValue(item)}`);
    });
  }

  return lines.join('\n');
}

/* =========================================================
   NORMALIZATION HELPERS
   ========================================================= */

export function normalizeFactsForPrompt(facts = []) {
  return (facts || []).map((fact) => ({
    fieldPath: fact.fieldPath || '',
    value: fact.value ?? null,
    status: fact.status || 'unknown',
    confidence: typeof fact.confidence === 'number' ? fact.confidence : 0,
    rationale: fact.rationale || [],
    reviewNeeded: !!fact.reviewNeeded,
    reviewReasons: fact.reviewReasons || [],
    sourceTypes: fact.sourceTypes || [],
    sourceRefs: fact.sourceRefs || [],
    fieldStatusInstruction: getFieldStatusInstruction(fact.status),
  }));
}

export function formatConfidence(confidence) {
  return `${Math.round((Number(confidence || 0) || 0) * 100)}%`;
}

export function stringifyPromptValue(value) {
  if (value == null) return 'null';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}